import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Settings, UserCog, Building2, Receipt, Globe, Database, Bell, ArrowLeft, AlertCircle, Server, CreditCard, Plug, ShieldCheck, Archive, Trash2, Smartphone, Wrench, History, Scale, PanelBottom } from "lucide-react";

import CompteSection from "./settings/CompteSection.jsx";
import MobileBarSection from "./settings/MobileBarSection.jsx";
import CabinetSection from "./settings/CabinetSection.jsx";
import FacturationSection from "./settings/FacturationSection.jsx";
import NotificationsSection from "./settings/NotificationsSection.jsx";
import PortailSection from "./settings/PortailSection.jsx";
import BackupSection from "./settings/BackupSection.jsx";
import SmtpSection from "./settings/SmtpSection.jsx";
import StripeSection from "./settings/StripeSection.jsx";
import IntegrationsSection from "./settings/IntegrationsSection.jsx";
import SmsSection from "./settings/SmsSection.jsx";
import LegalPagesSection from "./settings/LegalPagesSection.jsx";
import { TONE_BG } from "@/constants/tones";

// Each tile: id, label, short description, icon, accent color (Tailwind tone).
const TABS = [
  { id: "account",       label: "Compte",          desc: "Profil & 2FA",            icon: UserCog,         tone: "blue"    },
  { id: "cabinet",       label: "Cabinet",         desc: "Logo, coordonnées",       icon: Building2,       tone: "indigo"  },
  { id: "invoice",       label: "Facturation",     desc: "Tarifs & taxes",          icon: Receipt,         tone: "emerald" },
  { id: "stripe",        label: "Configuration Stripe", desc: "Loyers + Enquêtes crédit + Bail (compte production)", icon: CreditCard, tone: "violet" },
  { id: "smtp",          label: "Serveur SMTP",    desc: "Envoi de courriels",      icon: Server,          tone: "teal"    },
  { id: "notifications", label: "Paramètres de notifications", desc: "Canaux, événements, push", icon: Bell,         tone: "amber"   },
  { id: "sms",           label: "SMS",             desc: "Twilio — compteur & historique", icon: Smartphone,    tone: "emerald" },
  { id: "portal",        label: "Portail",         desc: "Accès clients",           icon: Globe,           tone: "cyan"    },
  { id: "mobile",        label: "Barre mobile",    desc: "Raccourcis en bas d'écran — admin & portail", icon: PanelBottom, tone: "cyan" },
  { id: "backup",        label: "Sauvegarde",      desc: "Automatique vers Google Drive · restauration", icon: Database, tone: "slate" },
  { id: "integrations",  label: "Mes intégrations", desc: "Stripe, Drive, SMTP, etc.", icon: Plug,          tone: "violet"  },
  { id: "legal",         label: "Pages légales",   desc: "Conditions d'utilisation · confidentialité", icon: Scale, tone: "slate" },
  { id: "security",      label: "Sécurité",        desc: "Audit, accès, journaux",   icon: ShieldCheck,     tone: "rose", link: "/admin/security" },
  { id: "audit",         label: "Piste d'audit",   desc: "Qui a modifié quoi, et quand", icon: History,     tone: "slate", link: "/admin/audit" },
  { id: "archive-bin",   label: "Archivage",       desc: "Dossiers cachés (réactivables)", icon: Archive,    tone: "amber", link: "/admin/archive-bin" },
  { id: "trash",         label: "Corbeille",       desc: "Avant suppression définitive", icon: Trash2,      tone: "rose", link: "/admin/clients-trash" },
  { id: "badges-diagnostic", label: "Diagnostic pastilles", desc: "État, reset, restaurer, tester", icon: Wrench, tone: "amber", link: "/admin/settings/diagnostic-pastilles" },
];


export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState({});
  // Batch #14.71/#14.82 — `tab` est dérivé DIRECTEMENT de l'URL (?tab=<id>).
  // Cliquer sur "Paramètres" dans le breadcrumb (qui mène à /admin/settings sans param)
  // ramène automatiquement à la grille. Pas de useState local pour éviter le conflit.
  const tab = searchParams.get("tab") || null;
  const setTab = (newTab) => {
    if (newTab) setSearchParams({ tab: newTab });
    else setSearchParams({});
  };
  const [alerts, setAlerts] = useState({}); // { tile_id: { count, reason } }

  const load = useCallback(async () => {
    try { const r = await api.get("/settings"); setSettings({ ...(r.data || {}), _ts: Date.now() }); }
    catch { /* silent */ }
  }, []);

  // Iter86 — Health endpoint backend (centralise badge logic)
  const loadAlerts = useCallback(async () => {
    try {
      const r = await api.get("/settings/health");
      const data = r.data || {};
      const next = {};
      // Map backend section -> tile id
      const map = {
        cabinet: "cabinet",
        invoice: "invoice",
        smtp: "cabinet",          // SMTP issues remontent dans le tile Cabinet
        notifications: "notifications",
        portal: "portal",
        push: "push",
      };
      Object.entries(data).forEach(([section, info]) => {
        if (section.startsWith("_") || !info || typeof info !== "object") return;
        if (info.valid === false) {
          const tileId = map[section] || section;
          const issues = info.issues || [];
          // Aggregate issues sur le meme tile
          if (next[tileId]) {
            next[tileId].count += issues.length || 1;
            next[tileId].reason += issues.length ? `; ${issues.join("; ")}` : "";
          } else {
            next[tileId] = {
              count: issues.length || 1,
              reason: issues.join(" ; ") || "Necessite votre attention",
            };
          }
        }
      });
      setAlerts(next);
    } catch {
      // Fallback silencieux : pas de badge si endpoint indisponible
      setAlerts({});
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Iter86 — health endpoint independant de settings local. Charge au mount + apres chaque save.
    loadAlerts();
    const handler = () => loadAlerts();
    window.addEventListener("settings:updated", handler);
    return () => window.removeEventListener("settings:updated", handler);
  }, [loadAlerts]);

  const update = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const save = async (fields, onSuccess) => {
    try {
      const payload = {};
      fields.forEach(k => {
        if (settings[k] !== undefined && settings[k] !== null && settings[k] !== "") {
          payload[k] = settings[k];
        } else if (settings[k] === "") {
          payload[k] = null;
        }
      });
      await api.put("/settings", payload);
      toast.success("Paramètres enregistrés");
      window.dispatchEvent(new CustomEvent("settings:updated", { detail: fields }));
      onSuccess?.();
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg).join(", "));
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur");
      }
    }
  };

  // ── Grid view (no submenu shown alongside) ─────────────────────────────
  if (!tab) {
    return (
      <div className="max-w-4xl mx-auto px-4" data-testid="settings-page">
        <h1 className="text-2xl font-bold mb-2 flex items-center gap-2 justify-center">
          <Settings className="w-6 h-6 text-gray-600" /> Paramètres
        </h1>
        <p className="text-center text-sm text-gray-500 mb-8">
          Sélectionnez une catégorie pour la configurer
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {TABS.map(t => {
            const alert = alerts[t.id];
            // Batch #14.68 — Tuile externe (link prop) navigue vers une page complète au lieu d'ouvrir une section inline.
            const className = `relative group bg-gradient-to-br ${TONE_BG[t.tone]} rounded-2xl p-5 sm:p-6 flex flex-col items-center justify-center text-center transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg border`;
            const tileContent = (
              <>
                {alert && (
                  <span
                    className="absolute top-2 right-2 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm ring-2 ring-white"
                    data-testid={`settings-alert-${t.id}`}
                    aria-label={`Alerte: ${alert.reason}`}
                  >
                    {alert.count > 9 ? "9+" : alert.count}
                  </span>
                )}
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-3 group-hover:shadow-md transition">
                  <t.icon className="w-7 h-7 sm:w-8 sm:h-8" />
                </div>
                <span className="font-semibold text-sm sm:text-base mb-0.5">{t.label}</span>
                <span className="text-[10px] sm:text-xs opacity-75 line-clamp-2">{alert ? alert.reason : t.desc}</span>
              </>
            );
            return t.link ? (
              <Link
                key={t.id}
                to={t.link}
                className={className}
                data-testid={`settings-tile-${t.id}`}
                title={alert ? alert.reason : t.desc}
              >
                {tileContent}
              </Link>
            ) : (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={className}
                data-testid={`settings-tile-${t.id}`}
                title={alert ? alert.reason : t.desc}
              >
                {tileContent}
              </button>
            );
          })}
        </div>

        {Object.keys(alerts).length > 0 && (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-900 max-w-2xl mx-auto" data-testid="settings-alerts-summary">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <strong>{Object.keys(alerts).length} catégorie(s)</strong> nécessite(nt) votre attention.
              Cliquez sur la tuile correspondante (badge rouge) pour la configurer.
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Section view (with back arrow) ─────────────────────────────────────
  const current = TABS.find(t => t.id === tab);

  return (
    <div className="max-w-3xl mx-auto" data-testid="settings-page">
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => setTab(null)}
          className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center transition active:scale-90"
          aria-label="Retour aux paramètres"
          data-testid="settings-back-btn">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold flex items-center gap-2">
          {current && <current.icon className="w-5 h-5 text-gray-600" />}
          {current?.label || "Paramètres"}
        </h1>
      </div>

      {tab === "account" && <CompteSection />}
      {tab === "mobile" && <MobileBarSection settings={settings} update={update} onSave={save} />}
      {tab === "cabinet" && <CabinetSection settings={settings} update={update} onSave={save} setSettings={setSettings} />}
      {tab === "invoice" && <FacturationSection settings={settings} update={update} onSave={save} />}
      {tab === "stripe" && <StripeSection />}
      {tab === "smtp" && <SmtpSection />}
      {tab === "sms" && <SmsSection />}
      {tab === "notifications" && <NotificationsSection />}
      {tab === "portal" && <PortailSection settings={settings} update={update} onSave={save} />}
      {tab === "backup" && <BackupSection />}
      {tab === "integrations" && <IntegrationsSection />}
      {tab === "legal" && <LegalPagesSection />}
    </div>
  );
}
